<template>
  <div class="pm-status">
    <div class="pm-status-head">
      <strong>{{ $t('partner-map.status.title') }}</strong>
      <div class="pm-presets">
        <b-button
          v-for="preset in presets"
          :key="preset.key"
          size="sm"
          variant="outline-secondary"
          @click="$emit('preset', preset.key)"
        >
          {{ preset.label }}
        </b-button>
      </div>
    </div>

    <div class="pm-chips">
      <button
        v-for="s in statuses"
        :key="s.key"
        type="button"
        class="pm-chip"
        :class="{ on: value.has(s.key) }"
        :aria-pressed="String(value.has(s.key))"
        :title="`${s.key} — ${$t('partner-map.group.' + s.group)}`"
        @click="$emit('toggle', s.key)"
      >
        <span class="box" />
        <span class="grp" :style="{ background: groupColor[s.group] }" />
        {{ $t('partner-map.job-status.' + statusLabelKey(s.key)) }}
        <span class="cnt">{{ fmt.num(s.jobs) }}</span>
      </button>
    </div>

    <p class="pm-status-foot" v-html="summary" />
  </div>
</template>

<script>
import { GROUP_COLOR, GROUP_ORDER, STATUS_META } from './model';

export default {
  name: 'PartnerMapStatusFilter',
  props: {
    /** All statuses present in the data, from `collectStatuses()`. */
    statuses: { type: Array, required: true },
    /** Currently selected status keys. */
    value: { type: Set, required: true },
    /** Pre-rendered summary sentence — the view owns the numbers. */
    summary: { type: String, default: '' },
    fmt: { type: Object, required: true },
  },
  data() {
    return { groupColor: GROUP_COLOR };
  },
  computed: {
    presets() {
      return [
        { key: 'default', label: this.$t('partner-map.preset.default') },
        ...GROUP_ORDER.map((g) => ({
          key: g,
          label: this.$t(`partner-map.group.${g}`),
        })),
        { key: 'all', label: this.$t('partner-map.preset.all') },
      ];
    },
  },
  methods: {
    statusLabelKey(key) {
      return (STATUS_META[key] || [key])[0];
    },
  },
};
</script>

<style lang="scss" scoped>
.pm-status-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.pm-presets {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.pm-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.pm-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px 3px 6px;
  font-size: 12px;
  line-height: 1.5;
  border: 1px solid var(--pm-border);
  border-radius: 999px;
  background: var(--pm-surface-1);
  color: var(--pm-ink-2);
  cursor: pointer;

  .box {
    width: 11px;
    height: 11px;
    border: 1.5px solid var(--pm-ink-3);
    border-radius: 3px;
    flex: 0 0 auto;
  }
  .grp {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex: 0 0 auto;
  }
  .cnt {
    color: var(--pm-ink-3);
    font-variant-numeric: tabular-nums;
  }

  &.on {
    border-color: var(--pm-partner);
    background: var(--pm-partner-wash);
    color: var(--pm-ink-1);

    .box {
      border-color: var(--pm-partner);
      background: var(--pm-partner);
      // Tick drawn with borders so the chip needs no icon component.
      position: relative;
    }
    .box::after {
      content: '';
      position: absolute;
      left: 2.5px;
      top: 0.5px;
      width: 3px;
      height: 6px;
      border: solid #fff;
      border-width: 0 1.8px 1.8px 0;
      transform: rotate(42deg);
    }
  }
}

.pm-status-foot {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--pm-ink-2);
}
</style>
